import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";

interface FaqItem {
  q: string;
  a: string;
}

const groups: { title: string; icon: string; items: FaqItem[] }[] = [
  {
    title: "阅读相关",
    icon: "ri-book-2-line",
    items: [
      {
        q: "如何按题材找到想看的韩漫？",
        a: "顶部导航栏和首页的「分类导航」板块提供了校园、奇幻、都市、恋爱、悬疑等题材入口，点击对应分类即可浏览该题材下的全部作品，并可按人气、评分、状态筛选。",
      },
      {
        q: "怎么快速定位某部具体作品？",
        a: "导航栏右上角提供了搜索框，输入漫画名称或作者名即可跨全站检索；也可以在分类页通过状态与排序进一步缩小范围。",
      },
      {
        q: "如何查看作品的更新进度？",
        a: "每部作品详情页都会标注「连载中」或「已完结」状态、总话数与最近更新时间；首页「最新更新」板块也会同步汇总当日更新的内容。",
      },
      {
        q: "「连载中」和「已完结」有什么区别？",
        a: "「连载中」表示作品仍在持续更新，详情页会展示最近更新的章节；「已完结」表示剧情已收尾，可以一次性看完全部章节。",
      },
    ],
  },
  {
    title: "评分与数据",
    icon: "ri-bar-chart-box-line",
    items: [
      {
        q: "评分是怎么计算的？",
        a: "评分由读者的真实收藏与阅读反馈综合计算得出，详情的评分会展示到小数点后一位，帮助你判断作品口碑。",
      },
      {
        q: "人气/阅读量数据代表什么？",
        a: "阅读量是基于累计浏览数据统计的热度指标，人气越高通常代表作品越受欢迎，适合作为快速挑选的参考。",
      },
    ],
  },
  {
    title: "收藏与平台",
    icon: "ri-settings-3-line",
    items: [
      {
        q: "如何收藏喜欢的作品？",
        a: "进入作品详情页，点击「加入书架」即可收藏，再次点击可取消；收藏状态会保存在你的浏览器本地，方便随时回来继续追更。",
      },
      {
        q: "网站支持哪些设备访问？",
        a: "本站采用响应式设计，电脑、平板与手机均可正常浏览，移动端导航会收起为菜单按钮，阅读与分类浏览体验一致顺畅。",
      },
      {
        q: "遇到问题或建议如何反馈？",
        a: "欢迎通过关于我们页面的服务邮箱与我们联系，或发送投稿合作邮件，我们会认真对待每一位读者的反馈。",
      },
    ],
  },
];

export default function Faq() {
  const [open, setOpen] = useState<Record<string, boolean>>({ "0-0": true });

  const toggle = (key: string) =>
    setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: groups.flatMap((g) =>
      g.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    ),
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <Navbar />
      <main>
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "常见问题" }]} />

        <section className="container-x py-8 md:py-10">
          <div className="mb-8 max-w-2xl">
            <p className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-400" /> FAQ
            </p>
            <h1 className="font-heading text-3xl font-bold text-foreground-950">
              常见问题
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              关于题材分类、阅读方式、评分数据与收藏功能的常见疑问，都在这里为你解答。
              如果仍未能解决你的问题，欢迎通过联系我们页面与我们沟通。
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {groups.map((g, gi) => (
                <div key={g.title} className="mb-8">
                  <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-foreground-950">
                    <i className={`${g.icon} text-primary-500`} />
                    {g.title}
                  </h2>
                  <div className="divide-y divide-background-200 rounded-lg border border-background-200 bg-background-50">
                    {g.items.map((item, i) => {
                      const key = `${gi}-${i}`;
                      const isOpen = !!open[key];
                      return (
                        <div key={key}>
                          <button
                            type="button"
                            onClick={() => toggle(key)}
                            aria-expanded={isOpen}
                            className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                          >
                            <span className="text-sm font-semibold text-foreground-950">{item.q}</span>
                            <i
                              className={`text-lg text-foreground-400 transition-transform duration-300 ${
                                isOpen ? "rotate-180 text-primary-500" : ""
                              } ${isOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                            />
                          </button>
                          <div
                            className={`grid transition-all duration-300 ${
                              isOpen ? "grid-rows-[1fr] pb-4 opacity-100" : "grid-rows-[0fr] opacity-0"
                            }`}
                          >
                            <div className="overflow-hidden">
                              <p className="px-5 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <aside className="h-fit rounded-lg border border-background-200 bg-background-100 p-6">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <i className="ri-lifebuoy-line text-primary-500" /> 需要更多帮助？
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                可以前往「关于我们」页面查看联系方式，或直接发送邮件给我们，我们会尽快回复。
              </p>
              <a href="mailto:service@91hanmanhua.com" className="btn-primary mt-4 h-10 w-full px-4 text-sm">
                <i className="ri-mail-send-line text-lg" /> 联系客服
              </a>
            </aside>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }}
      />
    </div>
  );
}